import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Settings, Share2, Camera, Users, Image, 
  Plus, Send, X, ChevronRight, Bell, Quote
} from 'lucide-react';
import Placard from '../components/Placard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

/**
 * ProfilePage - LikeKind Profile (FINAL EXECUTION DIRECTIVE - LOCKED)
 * 
 * Layout (top → bottom):
 * A. Main Profile Header (background image, photo, Connect button ABOVE handle)
 * B. About Me (short text)
 * C. Custom Expression Space (PAID MEMBERS ONLY - one image OR quote)
 * D. Activity (OWNER-ONLY - finite scroll)
 * E. Photos (grid)
 * F. Placard (static presence only)
 * G. Contacts list (private - connections replaced with contacts)
 * 
 * Rules:
 * - Handle only (no public real names)
 * - Activity is OWNER-ONLY - visitors see nothing
 * - Contacts (not Friends)
 * - Online status visible only to contacts
 * - No badges, no public stats
 * - Expression space: paid members only, no links, no analytics
 */

export default function ProfilePage() {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  
  const userId = paramUserId || currentUser?.user_id;
  
  const [profile, setProfile] = useState(null);
  const [placard, setPlacard] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityCursor, setActivityCursor] = useState(null);
  const [hasMoreActivity, setHasMoreActivity] = useState(true);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [contacts, setContacts] = useState([]);  // Renamed from circles per directive
  const [loading, setLoading] = useState(true);
  const [connectionStatus, setConnectionStatus] = useState({ status: 'none', can_request: true });
  const [connectingLoading, setConnectingLoading] = useState(false);
  
  // Expression space (paid members only)
  const [expression, setExpression] = useState(null);
  
  // Online status (contacts-only visibility)
  const [onlineStatus, setOnlineStatus] = useState(null);
  
  // Activity post input
  const [newPostText, setNewPostText] = useState('');
  const [newPostPhoto, setNewPostPhoto] = useState(null);
  const [posting, setPosting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Background/Photo editing
  const [editingBackground, setEditingBackground] = useState(false);
  const backgroundInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const isOwnProfile = currentUser?.user_id === userId;
  
  useEffect(() => {
    if (!paramUserId && !currentUser) {
      navigate('/auth');
      return;
    }
    
    if (userId) {
      fetchProfile();
      fetchPlacard();
      fetchActivity();
      fetchPhotos();
      fetchContacts();  // Renamed from fetchCircles per directive
      if (!isOwnProfile && currentUser) {
        fetchConnectionStatus();
      }
    }
  }, [userId, paramUserId, currentUser]);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/api/profile/${userId}`, { headers });
      setProfile(response.data.profile);
      // Expression space (paid members only)
      setExpression(response.data.expression);
      // Online status (contacts-only visibility per directive)
      setOnlineStatus(response.data.online_status);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacard = async () => {
    try {
      const res = await axios.get(`${API}/api/placard/${userId}`);
      setPlacard(res.data.placard);
    } catch (error) {
      // No placard found - that's fine
    }
  };

  const fetchActivity = async (cursor = null) => {
    if (loadingActivity) return;
    setLoadingActivity(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const url = cursor 
        ? `${API}/api/profile/${userId}/activity?cursor=${cursor}&limit=10`
        : `${API}/api/profile/${userId}/activity?limit=10`;
      const res = await axios.get(url, { headers });
      
      if (cursor) {
        setActivity(prev => [...prev, ...(res.data.posts || [])]);
      } else {
        setActivity(res.data.posts || []);
      }
      setActivityCursor(res.data.next_cursor);
      setHasMoreActivity(res.data.has_more);
    } catch (error) {
      console.error('Error fetching activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/api/photos/${userId}`, { headers });
      setPhotos(res.data.photos || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
    }
  };

  const fetchContacts = async () => {
    // Contacts list is PRIVATE per directive - only fetch for own profile
    if (!isOwnProfile) return;
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/api/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(res.data || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchConnectionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/connections/status/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch connection status:', error);
    }
  };

  // Handle "Contact @handle" / "Ready to connect"
  const handleContact = async () => {
    if (!currentUser) {
      toast.error('Please sign in to connect');
      navigate('/auth');
      return;
    }
    
    if (connectionStatus.status === 'connected') {
      // Already connected - could open messaging in future
      toast.info('You are already connected!');
      return;
    }
    
    if (connectionStatus.status === 'pending_sent') {
      toast.info('Connection request already sent');
      return;
    }
    
    if (connectionStatus.status === 'pending_received') {
      // Accept the request
      setConnectingLoading(true);
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/api/connections/request/${connectionStatus.request_id}/accept`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("You're now connected!");
        setConnectionStatus({ status: 'connected', can_request: false });
      } catch (error) {
        toast.error('Failed to accept connection');
      } finally {
        setConnectingLoading(false);
      }
      return;
    }
    
    // Send new connection request
    setConnectingLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/connections/request`, {
        to_user_id: userId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Connection request sent!');
      setConnectionStatus({ status: 'pending_sent', can_request: false });
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'membership_required') {
        toast.error('Membership required to connect');
        navigate('/membership');
      } else {
        toast.error(detail || 'Failed to send connection request');
      }
    } finally {
      setConnectingLoading(false);
    }
  };

  // Share profile - robust implementation that never throws
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  
  const shareProfile = async () => {
    const url = `${window.location.origin}/profile/${userId}`;
    const handle = profile?.username ? `@${profile.username}` : 'this profile';
    
    // Method 1: Try Web Share API (if available AND permitted)
    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ 
          title: `${handle} on LikeKind`, 
          text: `Check out ${handle} on LikeKind`,
          url 
        });
        return; // Success - exit early
      } catch (e) {
        // Share failed (permission denied, user cancelled, etc.)
        // Fall through to clipboard
      }
    }
    
    // Method 2: Try Clipboard API
    if (typeof navigator.clipboard?.writeText === 'function') {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Profile link copied!');
        return; // Success - exit early
      } catch (e) {
        // Clipboard failed (permission denied, etc.)
        // Fall through to modal
      }
    }
    
    // Method 3: Show modal with URL for manual copy
    setShareUrl(url);
    setShowShareModal(true);
  };

  const copyFromModal = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Profile link copied!');
      setShowShareModal(false);
    } catch (e) {
      // Even this failed - user must manually select and copy
      toast.info('Please select and copy the URL manually');
    }
  };

  // Post to Activity
  const handlePostActivity = async () => {
    if (!newPostText.trim() && !newPostPhoto) {
      toast.error('Add some text or a photo');
      return;
    }
    
    setPosting(true);
    try {
      const token = localStorage.getItem('token');
      
      // If there's a photo, upload it first
      let photoUrl = null;
      if (newPostPhoto) {
        const formData = new FormData();
        formData.append('file', newPostPhoto);
        const uploadRes = await axios.post(`${API}/api/upload/image`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        photoUrl = uploadRes.data.url;
      }
      
      await axios.post(`${API}/api/friends/posts`, {
        caption: newPostText,
        photos: photoUrl ? [photoUrl] : []
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Posted to your activity');
      setNewPostText('');
      setNewPostPhoto(null);
      fetchActivity(); // Refresh
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.error === 'membership_required') {
        toast.error('Membership required to post');
        navigate('/membership');
      } else {
        toast.error('Failed to post');
      }
    } finally {
      setPosting(false);
    }
  };

  // Update background image
  const handleBackgroundChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'background');
      
      const uploadRes = await axios.post(`${API}/api/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      await axios.put(`${API}/api/profile/update`, {
        background_image: uploadRes.data.url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(prev => ({ ...prev, background_image: uploadRes.data.url }));
      setEditingBackground(false);
      toast.success('Background updated!');
    } catch (error) {
      toast.error('Failed to update background');
    }
  };

  // Update profile photo
  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'profile');
      
      const uploadRes = await axios.post(`${API}/api/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      await axios.put(`${API}/api/profile/update`, {
        picture: uploadRes.data.url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setProfile(prev => ({ ...prev, picture: uploadRes.data.url }));
      toast.success('Photo updated!');
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  // Delete photo from gallery
  const handleDeletePhoto = async (photoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(prev => prev.filter(p => p.photo_id !== photoId));
      toast.success('Photo deleted');
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--fy-background-primary)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--lk-sage)]" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[var(--fy-background-primary)] flex items-center justify-center">
        <p className="text-slate-500">Profile not found</p>
      </div>
    );
  }

  const handle = profile.username ? `@${profile.username}` : '@user';
  const contactButtonText = connectionStatus.status === 'connected' 
    ? 'Connected' 
    : connectionStatus.status === 'pending_sent'
    ? 'Request Sent'
    : connectionStatus.status === 'pending_received'
    ? 'Accept Connection'
    : `Contact ${handle}`;

  return (
    <div className="min-h-screen bg-[var(--fy-background-primary)]">
      {/* Hidden file inputs */}
      <input 
        type="file" 
        ref={backgroundInputRef} 
        className="hidden" 
        accept="image/*"
        onChange={handleBackgroundChange}
      />
      <input 
        type="file" 
        ref={photoInputRef} 
        className="hidden" 
        accept="image/*"
        onChange={handlePhotoChange}
      />
      <input 
        type="file" 
        ref={fileInputRef} 
        className="hidden" 
        accept="image/*"
        onChange={(e) => setNewPostPhoto(e.target.files?.[0])}
      />

      {/* ═══════════════════════════════════════════════════════════
          A. MAIN PROFILE HEADER
          ═══════════════════════════════════════════════════════════ */}
      <Card className="relative overflow-hidden border-0 rounded-none md:rounded-2xl md:mx-4 md:mt-4">
        {/* Background Image */}
        <div 
          className="h-48 md:h-64 bg-gradient-to-br from-[var(--lk-sage)] to-[var(--lk-sage-dark)] relative"
          style={profile.background_image ? {
            backgroundImage: `url(${profile.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}}
        >
          {/* Settings icon (own profile only) */}
          {isOwnProfile && (
            <button
              onClick={() => navigate('/settings')}
              className="absolute top-4 left-4 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            >
              <Settings className="w-5 h-5 text-white" />
            </button>
          )}
          
          {/* Notifications icon - in top bar per directive */}
          {isOwnProfile && (
            <button
              onClick={() => navigate('/notifications')}
              className="absolute top-4 left-14 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
            >
              <Bell className="w-5 h-5 text-white" />
            </button>
          )}
          
          {/* Share button */}
          <button
            onClick={shareProfile}
            className="absolute top-4 left-24 p-2 rounded-full bg-black/30 hover:bg-black/50 transition-colors"
          >
            <Share2 className="w-5 h-5 text-white" />
          </button>
          
          {/* Online Status Indicator (visible to contacts) — calm presence, no pulse */}
          {onlineStatus?.online && (
            <div
              className="absolute top-4 right-32 md:right-36 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1.5"
              style={{ backgroundColor: 'var(--ground-sage)', color: '#fff', opacity: 0.9 }}
              data-testid="online-indicator"
            >
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#fff', opacity: 0.85 }} />
              Online
            </div>
          )}
          
          {/* Profile Photo (upper-right, slightly larger) */}
          <div className="absolute top-4 right-4">
            <button
              onClick={() => isOwnProfile && photoInputRef.current?.click()}
              className={`relative ${isOwnProfile ? 'cursor-pointer' : ''}`}
              disabled={!isOwnProfile}
            >
              <img
                src={profile.picture || '/default-avatar.png'}
                alt={handle}
                className="w-20 h-20 md:w-24 md:h-24 rounded-full border-4 border-white object-cover shadow-lg"
              />
              {isOwnProfile && (
                <div className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-[var(--lk-terra)] text-white shadow-md">
                  <Camera className="w-4 h-4" />
                </div>
              )}
            </button>
          </div>
          
          {/* Change background (own profile only) */}
          {isOwnProfile && (
            <button
              onClick={() => backgroundInputRef.current?.click()}
              className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-black/30 hover:bg-black/50 transition-colors text-white text-xs flex items-center gap-1"
            >
              <Image className="w-3 h-3" /> Change Background
            </button>
          )}
        </div>
        
        {/* Handle & Contact Button */}
        <div className="p-4 md:p-6 bg-white">
          {/* Connect Button - ABOVE handle per directive */}
          {!isOwnProfile && (
            <div className="mb-3 flex flex-wrap gap-2">
              {/* Connect - Opens Messaging App (integrated module) */}
              <button
                onClick={() => navigate(`/inbox?user=${userId}`)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--lk-sage)] text-white text-sm font-medium hover:bg-[var(--lk-sage-dark)] transition-colors"
                data-testid="connect-button"
              >
                <Send className="w-4 h-4" /> Connect
              </button>
              
              {/* Connection Status Button (for contact request flow) */}
              {currentUser && connectionStatus.status !== 'none' && (
                <Button
                  onClick={handleContact}
                  disabled={connectingLoading || connectionStatus.status === 'connected'}
                  variant="outline"
                  size="sm"
                >
                  {connectingLoading ? 'Connecting...' : 
                   connectionStatus.status === 'connected' ? 'Contact' :
                   connectionStatus.status === 'pending_sent' ? 'Request Sent' :
                   connectionStatus.status === 'pending_received' ? 'Accept Contact' : ''}
                </Button>
              )}
            </div>
          )}
          
          {/* Handle - displayed clearly under connect button */}
          <h1 className="text-xl md:text-2xl font-bold text-slate-800">{handle}</h1>
          
          {isOwnProfile && (
            <div className="mt-3">
              <Button
                onClick={() => navigate('/settings/profile')}
                variant="outline"
                className="w-full md:w-auto"
              >
                Edit Profile
              </Button>
            </div>
          )}
        </div>
      </Card>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* ═══════════════════════════════════════════════════════════
            A. ABOUT ME (editable, no defaults)
            ═══════════════════════════════════════════════════════════ */}
        <Card className="p-4 bg-white border border-slate-200">
          <h2 className="font-semibold text-slate-800 mb-2">About Me</h2>
          {profile.bio ? (
            <p className="text-slate-600 text-sm whitespace-pre-wrap">{profile.bio}</p>
          ) : isOwnProfile ? (
            <button
              onClick={() => navigate('/settings/profile')}
              className="text-sm text-[var(--lk-terra)] hover:underline"
            >
              Add something about yourself in Settings → Profile
            </button>
          ) : (
            <p className="text-slate-400 text-sm italic">No bio yet</p>
          )}
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            B. CUSTOM EXPRESSION SPACE (PAID MEMBERS ONLY)
            Per directive: One still image OR short quote. No links. No analytics.
            ═══════════════════════════════════════════════════════════ */}
        {(expression?.image_url || expression?.quote) && (
          <Card className="p-4 bg-white border border-slate-200">
            {expression.image_url ? (
              <img 
                src={expression.image_url} 
                alt="Expression" 
                className="w-full rounded-lg max-h-64 object-cover"
              />
            ) : expression.quote ? (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Quote className="w-6 h-6 text-[var(--lk-sage)] flex-shrink-0 mt-1" />
                <p className="text-slate-700 italic text-lg leading-relaxed">
                  "{expression.quote}"
                </p>
              </div>
            ) : null}
          </Card>
        )}
        
        {/* Expression Space prompt for paid members without one */}
        {isOwnProfile && !expression?.image_url && !expression?.quote && profile.membership_tier && profile.membership_tier !== 'observer' && (
          <Card className="p-4 bg-slate-50 border border-dashed border-slate-300">
            <div className="flex items-center gap-3">
              <Quote className="w-5 h-5 text-slate-400" />
              <div className="flex-1">
                <p className="text-sm text-slate-600">Add an expression</p>
                <p className="text-xs text-slate-400">One image or quote to personalize your profile</p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/settings/profile#expression')}
              >
                Add
              </Button>
            </div>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            C. ACTIVITY (OWNER-ONLY - visitors cannot see)
            ═══════════════════════════════════════════════════════════ */}
        {isOwnProfile && (
        <Card className="p-4 bg-white border border-slate-200">
          <h2 className="font-semibold text-slate-800 mb-4">Activity</h2>
          
          {/* Post Input (own profile only) */}
          {isOwnProfile && (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg">
              <textarea
                value={newPostText}
                onChange={(e) => setNewPostText(e.target.value)}
                placeholder="Share something..."
                className="w-full bg-transparent border-0 resize-none text-sm focus:outline-none"
                rows={2}
              />
              {newPostPhoto && (
                <div className="relative mt-2 inline-block">
                  <img 
                    src={URL.createObjectURL(newPostPhoto)} 
                    alt="Preview" 
                    className="h-20 w-auto rounded"
                  />
                  <button
                    onClick={() => setNewPostPhoto(null)}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 rounded-full text-white"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-slate-500 hover:text-[var(--lk-terra)] text-sm"
                >
                  <Camera className="w-4 h-4" /> Photo
                </button>
                <Button
                  onClick={handlePostActivity}
                  disabled={posting || (!newPostText.trim() && !newPostPhoto)}
                  size="sm"
                  className="bg-[var(--lk-sage)] hover:bg-[var(--lk-sage-dark)] text-white"
                >
                  {posting ? 'Posting...' : <><Send className="w-4 h-4 mr-1" /> Post</>}
                </Button>
              </div>
            </div>
          )}
          
          {/* Activity Feed (Finite Scroll) */}
          <div className="space-y-4">
            {activity.length === 0 ? (
              <p className="text-center text-slate-400 py-8 text-sm">
                {isOwnProfile ? "Share your first update with connections" : "No activity yet"}
              </p>
            ) : (
              <>
                {activity.map(post => (
                  <div key={post.post_id} className="pb-4 border-b border-slate-100 last:border-0">
                    <p className="text-sm text-slate-700">{post.caption}</p>
                    {post.photos?.length > 0 && (
                      <div className="mt-2 flex gap-2 overflow-x-auto">
                        {post.photos.map((photo, idx) => (
                          <img 
                            key={idx}
                            src={photo}
                            alt=""
                            className="h-32 w-auto rounded"
                          />
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-slate-400 mt-2">
                      {new Date(post.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
                
                {/* Load More / End State */}
                {hasMoreActivity ? (
                  <button
                    onClick={() => fetchActivity(activityCursor)}
                    disabled={loadingActivity}
                    className="w-full py-2 text-sm text-[var(--lk-sage)] hover:underline"
                  >
                    {loadingActivity ? 'Loading...' : 'Load more'}
                  </button>
                ) : activity.length > 0 && (
                  <p className="text-center text-slate-400 text-sm py-4">
                    ✓ You're caught up
                  </p>
                )}
              </>
            )}
          </div>
          
          <p className="text-xs text-slate-400 mt-4 text-center">
            Private - only you can see your activity
          </p>
        </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            C. PHOTOS
            ═══════════════════════════════════════════════════════════ */}
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-800">Photos</h2>
            {isOwnProfile && (
              <button
                onClick={() => navigate(`/profile/${userId}/photos`)}
                className="text-sm text-[var(--lk-terra)] hover:underline flex items-center"
              >
                Manage <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {photos.length === 0 ? (
            <p className="text-center text-slate-400 py-8 text-sm">
              {isOwnProfile ? (
                <button
                  onClick={() => navigate(`/profile/${userId}/photos`)}
                  className="text-[var(--lk-terra)] hover:underline"
                >
                  Add your first photo
                </button>
              ) : "No photos yet"}
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.slice(0, 6).map(photo => (
                <div key={photo.photo_id} className="relative aspect-square">
                  <img
                    src={photo.url}
                    alt=""
                    className="w-full h-full object-cover rounded"
                  />
                </div>
              ))}
            </div>
          )}
          
          {photos.length > 6 && (
            <button
              onClick={() => navigate(`/profile/${userId}/photos`)}
              className="w-full mt-3 py-2 text-sm text-[var(--lk-sage)] hover:underline"
            >
              View all {photos.length} photos
            </button>
          )}
        </Card>

        {/* ═══════════════════════════════════════════════════════════
            E. PLACARD
            ═══════════════════════════════════════════════════════════ */}
        {(placard || isOwnProfile) && (
          <Card className="p-4 bg-white border border-slate-200">
            <h2 className="font-semibold text-slate-800 mb-4">Placard</h2>
            <Placard 
              placard={placard} 
              isOwner={isOwnProfile}
              onEdit={() => navigate('/settings/profile')}
            />
          </Card>
        )}

        {/* ═══════════════════════════════════════════════════════════
            G. CONTACTS (Private - owner-only view per directive)
            ═══════════════════════════════════════════════════════════ */}
        {isOwnProfile && (
          <Card className="p-4 bg-white border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-slate-800">Contacts</h2>
              <span className="text-sm text-slate-400">{contacts.length} contacts</span>
            </div>
            
            {contacts.length === 0 ? (
              <p className="text-center text-slate-400 py-8 text-sm">
                No contacts yet. Connect with people to add them here.
              </p>
            ) : (
              <div className="flex flex-wrap gap-3">
                {contacts.slice(0, 12).map(contact => (
                  <button
                    key={contact.user_id}
                    onClick={() => navigate(`/profile/${contact.user_id}`)}
                    className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="relative">
                      <img
                        src={contact.picture || '/default-avatar.png'}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      {/* Online indicator - visible only to contacts per directive */}
                      {contact.online && (
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-slate-700">
                      {contact.handle || `@${contact.user_id.slice(0, 8)}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
            
            {contacts.length > 12 && (
              <button
                onClick={() => navigate('/contacts')}
                className="w-full mt-3 py-2 text-sm text-[var(--lk-sage)] hover:underline"
              >
                View all contacts
              </button>
            )}
          </Card>
        )}
      </div>

      {/* Share Modal (fallback when clipboard/share APIs fail) */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm p-6 bg-white">
            <h3 className="font-semibold text-slate-800 mb-2">Share Profile</h3>
            <p className="text-sm text-slate-500 mb-4">Copy this link to share:</p>
            <input
              type="text"
              value={shareUrl}
              readOnly
              className="w-full p-3 bg-slate-100 rounded border text-sm font-mono select-all"
              onClick={(e) => e.target.select()}
            />
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowShareModal(false)}
              >
                Close
              </Button>
              <Button
                className="flex-1 bg-[var(--lk-sage)] text-white"
                onClick={copyFromModal}
              >
                Copy Link
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
